import React, { FC } from 'react';
import { NotificationBanner } from 'src/components/widgets/notificationBanner';
import { Link } from 'src/components/widgets/buttons';
import { T } from 'src/shared/helpers';
import { DiscoveryAiCreditState } from './useDiscoveryAiCredits';

// Matches getVectorizeServiceConfig().learnMoreUrl (serviceCreditsConfigs.tsx).
const VECTORIZER_GUIDE_URL = 'https://zakeke.zendesk.com/hc/en-us/articles/22673500547996-Vectorizer';
// The AI Credits and Billing help article, the same link of the other AI credits notices (AiCreditsNotice).
const AI_CREDITS_BILLING_URL = 'https://zakeke.zendesk.com/hc/en-us/articles/31154048227740-AI-Credits-and-Billing';

interface DiscoveryVectorizeBannerProps {
    /**
     * True when the seller's plan cannot use the AI Vectorizer at all (no `additionalFeatures.vectorization`,
     * or a Shopify development store in production). Takes precedence over the credit case — activating would
     * be refused by the backend, so the only useful action is an upgrade.
     */
    planUnsupported: boolean;
    /**
     * The seller's resolved VECTORIZE credit situation, or null when it hasn't been resolved yet (which is
     * always the case while `planUnsupported`, since the host skips the query).
     */
    credits: DiscoveryAiCreditState | null;
    /** Grants the one-time free trial pack (auto-refill off). The parent reboots the iframe on success. */
    onActivate: () => void;
    /** True while the grant mutation is in flight — disables the button to prevent a double activation. */
    isActivating: boolean;
    /** Opens the buy-credits dialog for the VECTORIZE pool. */
    onBuy: () => void;
    /** Opens the shared plan-upgrade dialog (useUpgradeDialogs), which owns the route to Subscription. */
    onUpgrade: () => void;
}

/**
 * Discovery-mode banner for the AI Vectorizer printing method. It renders in the three states where the seller
 * cannot simply use the tool, mirroring how the AI Creative Tools banner behaves:
 *
 *  - plan unsupported — the plan doesn't include the feature: offer an upgrade. The pill is deliberately NOT
 *    hidden in this case: discovery exists to show merchants what they don't have yet, so a feature their plan
 *    lacks is exactly what it should surface — with an honest way to get it.
 *  - `needs-activation` — never activated: explain it is billed via credits and offer the free trial.
 *  - `out-of-credits` — the trial (or last pack) is spent: offer to buy the next one.
 *  - `has-credits` — nothing. See the comment at the return below.
 *
 * Deliberately says nothing about a per-image cost: the balance is a Decimal and a vectorization deducts a
 * fraction of a credit, which nothing on the frontend knows.
 */
const DiscoveryVectorizeBanner: FC<DiscoveryVectorizeBannerProps> = ({
    planUnsupported,
    credits,
    onActivate,
    isActivating,
    onBuy,
    onUpgrade
}) => {
    if (planUnsupported) {
        return (
            <NotificationBanner
                type='WARNING'
                variant='extended'
                content={
                    <>
                        <strong>{T._("Your plan doesn't include the AI Vectorizer", 'Admin')}</strong>
                        {'\n'}
                        {T._(
                            'It automatically turns uploaded images into clean vector artwork. Upgrade your plan to try it on this product and enable it for your customers — the free trial credits are included once your plan covers it, so there is nothing extra to buy to try it out.',
                            'Admin'
                        )}{' '}
                        <Link href={VECTORIZER_GUIDE_URL} target='_blank'>
                            {T._('Learn more', 'Admin')}
                        </Link>
                    </>
                }
                buttons={[{ label: T._('Upgrade plan', 'Admin'), onClick: onUpgrade }]}
            />
        );
    }

    // Not resolved yet (the host queries on discovery entry and on each engagement).
    if (!credits) {
        return null;
    }

    if (credits.creditCase === 'needs-activation') {
        return (
            <NotificationBanner
                type='AI'
                variant='extended'
                content={
                    <>
                        <strong>{T._('This printing method is billed via credits', 'Admin')}</strong>
                        {'\n'}
                        {T._('Each output you generate uses AI credits. You start with free credits, then usage is billed automatically.', 'Admin')}{' '}
                        <Link href={AI_CREDITS_BILLING_URL} target='_blank'>
                            {T._('How AI credits work', 'Admin')}
                        </Link>
                        .
                    </>
                }
                buttons={[
                    {
                        label: isActivating ? T._('Activating…', 'Admin') : T._('Activate free credits', 'Admin'),
                        onClick: onActivate,
                        disabled: isActivating
                    }
                ]}
            />
        );
    }

    if (credits.creditCase === 'out-of-credits') {
        return (
            <NotificationBanner
                type='WARNING'
                variant='extended'
                content={
                    <>
                        <strong>{T._('Your AI Vectorizer credits are used up', 'Admin')}</strong>
                        {'\n'}
                        {T._(
                            'The printing method still applies, but images will no longer be vectorized. Purchase a credit pack to keep using the AI Vectorizer here and in your live store.',
                            'Admin'
                        )}
                    </>
                }
                buttons={[{ label: T._('Buy credits', 'Admin'), onClick: onBuy }]}
            />
        );
    }

    // has-credits: no banner. The seller can use the tool, and the warning that using it spends real credits
    // belongs where the spending happens — the customizer's own credit-confirmation dialog on upload
    // (useAICreditConfirmation('ai-vectorize')), which is the same convention the AI Creative Tools follow.
    // A permanent balance strip here would just be noise above a working feature.
    return null;
};

export default DiscoveryVectorizeBanner;
